import { APIClientServices } from "./APIClientServices";

// ── DTOs (alinhados com backend PublicDTO) ────────────────────────

export interface MedSpecialtyDTO {
	id: string;
	workspaceId: string;
	name: string;
	slug: string;
	description: string | null;
	icon: string;
	color: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface MedAgentDTO {
	id: string;
	workspaceId: string;
	specialtyId: string;
	agentType: string;
	role: string;
	name: string;
	avatarUrl: string | null;
	systemPrompt: string;
	model: string;
	temperature: number;
	maxTokens: number;
	capabilities: string[];
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	specialtyName?: string;
	specialtySlug?: string;
	specialtyIcon?: string;
	specialtyColor?: string;
}

export interface MedPatientDTO {
	id: string;
	workspaceId: string;
	fullName: string;
	email: string | null;
	phone: string | null;
	dateOfBirth: string | null;
	gender: string | null;
	cpf: string | null;
	address: string | null;
	insuranceProvider: string | null;
	insuranceNumber: string | null;
	medicalHistory: Record<string, any> | null;
	allergies: string[];
	medications: string[];
	createdAt: string;
	updatedAt: string;
}

export interface MedMessageDTO {
	id: string;
	role: string;
	content: string;
	createdAt: string;
}

export interface MedConversationDTO {
	conversationId: string;
	messages: MedMessageDTO[];
}

/** Alinhado com PublicMedAppointmentDTO do backend */
export interface MedAppointmentDTO {
	id: string;
	workspaceId: string;
	patientId: string;
	doctorId: string;
	specialtyId: string | null;
	title: string;
	description: string | null;
	scheduledAt: string;
	durationMinutes: number;
	status: string;
	notes: string | null;
	createdAt: string;
	updatedAt: string;
}

/** Alinhado com PublicMedRecordDTO do backend */
export interface MedRecordDTO {
	id: string;
	workspaceId: string;
	patientId: string;
	doctorId: string;
	appointmentId: string | null;
	recordType: string;
	title: string;
	content: string;
	structuredData: Record<string, any> | null;
	attachments: string[];
	createdAt: string;
	updatedAt: string;
}

// ── Payloads (snake_case para o backend) ──────────────────────────

export interface CreatePatientPayload {
	full_name: string;
	email?: string;
	phone?: string;
	date_of_birth?: string;
	gender?: string;
	cpf?: string;
}

export interface CreateAppointmentPayload {
	patient_id: string;
	title: string;
	scheduled_at: string;
	duration_minutes?: number;
	description?: string;
	specialty_id?: string;
}

export interface CreateRecordPayload {
	patient_id: string;
	title: string;
	content: string;
	record_type?: string;
	structured_data?: Record<string, any>;
}

export interface MedDashboardKPIs {
	appointmentsToday: number;
	appointmentsTodayChange: number;
	patientsTotal: number;
	patientsTotalChange: number;
	avgDuration: number;
	avgDurationChange: number;
	adherence: number;
	adherenceChange: number;
}

export interface MedDashboardActivity {
	id: string;
	time: string;
	patient: string;
	type: string;
	specialty: string | null;
	status: string;
}

export interface MedDashboardDTO {
	kpis: MedDashboardKPIs;
	recentActivity: MedDashboardActivity[];
}

export interface MedAppointmentDetailDTO {
	appointment: MedAppointmentDTO;
	patientName: string;
	records: MedRecordDTO[];
}

// ── Service ───────────────────────────────────────────────────────

export class MedDoctorServices {
	private static readonly BASE_ROUTE = "v1/medical";

	// ── Especialidades ────────────────────────────────────────────

	static async listSpecialties(): Promise<MedSpecialtyDTO[]> {
		return APIClientServices.get<MedSpecialtyDTO[]>(`${this.BASE_ROUTE}/doctor/specialties`);
	}

	static async initializeSpecialties(): Promise<MedSpecialtyDTO[]> {
		return APIClientServices.post<MedSpecialtyDTO[]>(`${this.BASE_ROUTE}/doctor/specialties/initialize`);
	}

	// ── Agentes IA ────────────────────────────────────────────────

	static async listAgents(): Promise<MedAgentDTO[]> {
		return APIClientServices.get<MedAgentDTO[]>(`${this.BASE_ROUTE}/doctor/agents`);
	}

	// ── Chat com Agente ───────────────────────────────────────────

	static async sendMessage(agentId: string, content: string): Promise<{ userMessage: MedMessageDTO; assistantMessage: MedMessageDTO }> {
		return APIClientServices.post(`${this.BASE_ROUTE}/doctor/agents/${agentId}/messages`, { content });
	}

	static async getConversation(agentId: string): Promise<MedConversationDTO> {
		return APIClientServices.get<MedConversationDTO>(`${this.BASE_ROUTE}/doctor/agents/${agentId}/conversation`);
	}

	// ── Pacientes ─────────────────────────────────────────────────

	static async listPatients(): Promise<MedPatientDTO[]> {
		return APIClientServices.get<MedPatientDTO[]>(`${this.BASE_ROUTE}/doctor/patients`);
	}

	static async createPatient(data: CreatePatientPayload): Promise<MedPatientDTO> {
		return APIClientServices.post<MedPatientDTO>(`${this.BASE_ROUTE}/doctor/patients`, data);
	}

	static async getPatient(patientId: string): Promise<MedPatientDTO> {
		return APIClientServices.get<MedPatientDTO>(`${this.BASE_ROUTE}/doctor/patients/${patientId}`);
	}

	// ── Consultas ─────────────────────────────────────────────────

	static async listAppointments(): Promise<MedAppointmentDTO[]> {
		return APIClientServices.get<MedAppointmentDTO[]>(`${this.BASE_ROUTE}/doctor/appointments`);
	}

	static async createAppointment(data: CreateAppointmentPayload): Promise<MedAppointmentDTO> {
		return APIClientServices.post<MedAppointmentDTO>(`${this.BASE_ROUTE}/doctor/appointments`, data);
	}

	// ── Prontuários ───────────────────────────────────────────────

	static async listRecords(): Promise<MedRecordDTO[]> {
		return APIClientServices.get<MedRecordDTO[]>(`${this.BASE_ROUTE}/doctor/records`);
	}

	static async createRecord(data: CreateRecordPayload): Promise<MedRecordDTO> {
		return APIClientServices.post<MedRecordDTO>(`${this.BASE_ROUTE}/doctor/records`, data);
	}

	// ── Dashboard ─────────────────────────────────────────────────

	static async getDashboardStats(): Promise<MedDashboardDTO> {
		return APIClientServices.get<MedDashboardDTO>(`${this.BASE_ROUTE}/doctor/dashboard`);
	}

	// ── Detalhe da Consulta ───────────────────────────────────────

	static async getAppointmentDetail(appointmentId: string): Promise<MedAppointmentDetailDTO> {
		return APIClientServices.get<MedAppointmentDetailDTO>(`${this.BASE_ROUTE}/doctor/appointments/${appointmentId}`);
	}

	static async saveTranscription(appointmentId: string, content: string): Promise<MedRecordDTO> {
		return APIClientServices.post<MedRecordDTO>(`${this.BASE_ROUTE}/doctor/appointments/${appointmentId}/transcription`, { content });
	}
	static async analyzeTranscription(appointmentId: string): Promise<MedRecordDTO> {
		return APIClientServices.post<MedRecordDTO>(`${this.BASE_ROUTE}/doctor/appointments/${appointmentId}/analyze`);
	}

	// ── Teleconsulta ──────────────────────────────────────────────

	static async createTeleconsultationAppointment(
		patientUserId: string,
		patientName: string,
		reason: string,
	): Promise<MedAppointmentDTO> {
		return APIClientServices.post<MedAppointmentDTO>(
			`${this.BASE_ROUTE}/doctor/teleconsultation-appointment`,
			{ patientUserId, patientName, reason },
		);
	}
}
